import React, { useState, useEffect } from 'react';
import { DOMAIN_CONFIG } from '../config';
import { Check, Copy, Menu, X, Sparkles } from 'lucide-react';

interface HeaderProps {
  onOpenOfferModal?: () => void;
}

export const Header: React.FC<HeaderProps> = () => {
  const [scrolled, setScrolled] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const copyDomain = () => {
    navigator.clipboard.writeText(DOMAIN_CONFIG.domainName);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#040711]/85 backdrop-blur-md border-b border-slate-800/80 shadow-lg shadow-black/20 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo / Domain Pill */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollToSection('hero')}
              className="flex items-center gap-2 group text-left focus:outline-none"
              title="Return to top"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 group-hover:border-emerald-400 transition-colors shadow-sm shadow-emerald-500/10">
                <Sparkles className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-lg text-white group-hover:text-emerald-400 transition-colors tracking-tight">
                    {DOMAIN_CONFIG.domainName}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-[11px] font-medium text-emerald-400/90 tracking-wide uppercase">
                    Assessing Market Interest
                  </span>
                </div>
              </div>
            </button>

            {/* Quick Copy domain button */}
            <button
              onClick={copyDomain}
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono text-slate-400 hover:text-slate-200 bg-slate-900/60 hover:bg-slate-800 border border-slate-800 rounded-lg transition-all"
              title="Copy domain name to clipboard"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-300">
            <button
              onClick={() => scrollToSection('why-this-domain')}
              className="hover:text-emerald-400 transition-colors cursor-pointer"
            >
              Why This Domain
            </button>
            <button
              onClick={() => scrollToSection('use-cases')}
              className="hover:text-emerald-400 transition-colors cursor-pointer"
            >
              Blueprint Use Cases
            </button>
            <button
              onClick={() => scrollToSection('transfer-process')}
              className="hover:text-emerald-400 transition-colors cursor-pointer"
            >
              Escrow & Transfer
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="hover:text-emerald-400 transition-colors cursor-pointer"
            >
              FAQ
            </button>
          </nav>

          {/* Desktop Right CTA */}
          <div className="hidden sm:flex items-center gap-3">
            <button
              onClick={() => scrollToSection('offer-form')}
              className="relative group overflow-hidden rounded-xl p-[1px] focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500 rounded-xl opacity-90 group-hover:opacity-100 transition-opacity blur-[1px]"></span>
              <span className="relative flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-950 bg-gradient-to-r from-emerald-400 to-teal-300 rounded-[11px] transition-transform duration-200 group-hover:scale-[0.99]">
                {DOMAIN_CONFIG.enableOfferForm ? 'Make an Offer' : 'Contact Owner'}
                <span className="text-slate-900 group-hover:translate-x-0.5 transition-transform">→</span>
              </span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => scrollToSection('offer-form')}
              className="px-3 py-1.5 text-xs font-semibold text-slate-950 bg-emerald-400 hover:bg-emerald-300 rounded-lg transition-colors"
            >
              {DOMAIN_CONFIG.enableOfferForm ? 'Offer' : 'Contact'}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 focus:outline-none"
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#070b18]/95 backdrop-blur-xl border-b border-slate-800 px-4 pt-4 pb-6 mt-3 shadow-2xl animate-in fade-in slide-in-from-top-4 duration-200">
          <nav className="flex flex-col gap-3 text-base">
            <button
              onClick={() => scrollToSection('why-this-domain')}
              className="text-left py-2 px-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/40 rounded-lg"
            >
              Why This Domain
            </button>
            <button
              onClick={() => scrollToSection('use-cases')}
              className="text-left py-2 px-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/40 rounded-lg"
            >
              Blueprint Use Cases
            </button>
            <button
              onClick={() => scrollToSection('transfer-process')}
              className="text-left py-2 px-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/40 rounded-lg"
            >
              Escrow & Transfer
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="text-left py-2 px-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/40 rounded-lg"
            >
              FAQ
            </button>
            <div className="pt-2 border-t border-slate-800/80">
              <button
                onClick={() => scrollToSection('offer-form')}
                className="w-full text-center py-2.5 px-4 font-semibold text-slate-950 bg-emerald-400 hover:bg-emerald-300 rounded-xl shadow-md transition-colors"
              >
                {DOMAIN_CONFIG.enableOfferForm ? 'Submit Acquisition Offer' : 'Contact Domain Owner'}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
